import { autoinject, ComponentAttached } from "aurelia-framework";
import {
  RoutableComponentCanActivate,
  RedirectToRoute,
  RouteConfig,
  NavigationInstruction,
  NavigationCommand,
} from "aurelia-router";
import {
  ValidationControllerFactory,
  ValidationController,
  ValidationRules,
  ControllerValidateResult,
} from "aurelia-validation";
import { I18N } from "aurelia-i18n";
import { ApplicationState } from "../../services/application-state";
import { ProductfinderResult } from "./productfinder-result";
import {
  ProductfinderCustomerData,
  ProductfinderOfferData,
} from "./productfinder-offer-data";
import { BootstrapFormRenderer } from "../../validation/bootstrap-form-renderer";
import { ProductfinderService } from "./productfinder-service";
import { DialogFactory } from "../../services/dialog-factory";
import { ProductfinderData } from "./productfinder-data";
import { LocaleService } from "../../services/locale-service";

@autoinject
export class ProductfinderOffer
  implements ComponentAttached, RoutableComponentCanActivate
{
  customerData: ProductfinderCustomerData;
  offerSending: boolean = false;
  offerSent: boolean = false;
  form: HTMLFormElement;

  private result: ProductfinderResult;
  private validationController: ValidationController;

  constructor(
    private i18n: I18N,
    private state: ApplicationState,
    private service: ProductfinderService,
    private localeService: LocaleService,
    private validationControllerFactory: ValidationControllerFactory,
    private dialogFactory: DialogFactory,
  ) {
    this.result = state.productfinderResult;
    this.customerData = this.loadDataFromState();
    this.validationController =
      this.validationControllerFactory.createForCurrentScope();
    this.validationController.addRenderer(new BootstrapFormRenderer());

    type D = ProductfinderCustomerData;
    let tr = function (s: string) {
      return i18n.tr("productfinder.offer." + s);
    };

    ValidationRules.ensure((d: D) => d.name)
      .displayName(tr("name"))
      .required()

      .ensure((d: D) => d.email)
      .displayName(tr("email"))
      .required()
      .then()
      .email()

      .ensure((d: D) => d.quantity)
      .displayName(tr("quantity"))
      .satisfiesRule("int")
      .then()
      .satisfiesRule("positive")
      .satisfiesRule("max", 1000000)

      .ensure((d: D) => d.datenschutz)
      .displayName(tr("datenschutz"))
      .satisfies((b: boolean) => !!b)
      .withMessage(tr("datenschutz_required"))

      .on(this.customerData);
  }

  canActivate(
    params: any,
    routeConfig: RouteConfig,
    navigationInstruction: NavigationInstruction,
  ): boolean | NavigationCommand {
    // if someone tries to activate this component/route without having
    // a valid result then redirect to an error page
    if (!this.result) {
      return new RedirectToRoute("noresult");
    }
    return true;
  }

  attached(): void {
    // Save the application state each time any element in the form is blurred!
    this.form.addEventListener(
      "blur",
      (ev) => (this.state.productfinderCustomerData = this.customerData),
      /*useCapture=*/ true,
    );
    this.offerSent = this.offerSending = false;
  }

  submit(): void {
    if (this.offerSending || this.offerSent) return;
    this.validationController
      .validate()
      .then((result: ControllerValidateResult) => {
        if (!result.valid) return;
        this.offerSending = true;
        let customerDataClone = this.customerData.createCopyForServer();
        let inputDataClone = new ProductfinderData(
          this.state.productfinderData,
        ).createCopyForServer(this.localeService);
        let pumpen = this.state.productfinderResult.pumpen!;
        let productfinderOfferData = new ProductfinderOfferData(
          customerDataClone,
          inputDataClone,
          pumpen,
        );
        this.state.productfinderCustomerData = customerDataClone;
        this.service
          .offer(productfinderOfferData)
          .then((r) => {
            this.offerSent = true;
            this.offerSending = false;
          })
          .catch((e) =>
            this.dialogFactory.openError(
              this.i18n.tr("error.unexpected", { errorNo: e.errorNo }),
            ),
          );
      })
      .catch((e) => console.log("unhandled rejection during validation", e));
  }

  private loadDataFromState() {
    try {
      const customerData =
        this.state.productfinderCustomerData || new ProductfinderCustomerData();
      customerData.datenschutz = false;
      return customerData;
    } catch (e) {
      console.error("could not load state from storage", e);
      return new ProductfinderCustomerData();
    }
  }

  get note(): string {
    let key = "productfinder.notes.offer";

    let note = this.i18n.tr(key);

    if (note === key) return "";

    return note;
  }
}
